import { Tabs, TabsList, TabsTrigger } from '@sr/ui-library';
import { useQueries } from '@tanstack/react-query';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { queryOptionsGetTasksPaginated } from '../../../api/task/taskApi';
import { CountBadge } from '../../../components/CountBadge';
import { EmbeddedWorkflowView } from '../../../components/EmbeddedWorkflowView';
import { Spinner } from '../../../components/Loader';
import { PageHeader } from '../../../components/PageHeader';
import { useEmbeddedWorkflowActive } from '../../../hooks/useEmbeddedWorkflowActive';
import { useExcludedTaskCounts } from '../../../hooks/useExcludedTaskCounts';
import { MSR3BaseLayout } from '../../../layout/MSR3BaseLayout';
import { TASK_TABS, TaskTabSegment, taskTabPath } from './taskTabs';

const segmentFromPathname = (pathname: string): TaskTabSegment => {
  const raw = pathname.match(/\/tasks\/([^/]+)/)?.[1];

  return TASK_TABS.some((tab) => tab.segment === raw)
    ? (raw as TaskTabSegment)
    : 'potential';
};

/**
 * MSR3 shell for the Tasks page: {@link PageHeader} with a three-tab bar
 * (Potential Claims / Competing Claims / Drafts) carrying live count badges,
 * and an outlet for the active tab's list. Each tab is a sub-route so the tab
 * state lives in the URL path and Home's "View All" buttons can deep-link into
 * a specific tab. When a task's embedded workflow is open (`?src=`), the iframe
 * view replaces the tab content, mirroring the Home page.
 */
export const TasksLayout = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const isEmbeddingWorkflow = useEmbeddedWorkflowActive();

  const activeSegment = segmentFromPathname(location.pathname);

  // One lightweight count query per type so every tab badge reflects the total
  // number of that task type, independent of the active tab or its search term.
  const countQueries = useQueries({
    queries: TASK_TABS.map((tab) => ({
      ...queryOptionsGetTasksPaginated({
        pageSize: 1,
        type: tab.type,
        workspaceId,
      }),
    })),
  });

  const excludedCounts = useExcludedTaskCounts(workspaceId);

  return (
    <MSR3BaseLayout
      pageHeader={
        <PageHeader title="Tasks">
          <Tabs
            className="w-fit"
            value={activeSegment}
            onValueChange={(value) =>
              workspaceId &&
              navigate(taskTabPath(workspaceId, value as TaskTabSegment))
            }
          >
            <TabsList>
              {TASK_TABS.map((tab, index) => {
                const query = countQueries[index];
                const excludedCount = tab.countExcludedStatuses?.length
                  ? excludedCounts?.get(tab.type)
                  : 0;
                const count =
                  (query?.data?.meta?.totalItems ?? 0) - (excludedCount ?? 0);

                return (
                  <TabsTrigger key={tab.segment} value={tab.segment}>
                    {tab.label}
                    {query?.isLoading || excludedCount === undefined ? (
                      <Spinner className="size-4 text-muted-foreground" />
                    ) : (
                      count > 0 && <CountBadge>{count}</CountBadge>
                    )}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>
        </PageHeader>
      }
    >
      {isEmbeddingWorkflow ? <EmbeddedWorkflowView /> : <Outlet />}
    </MSR3BaseLayout>
  );
};
